import {
  Button,
  DateField,
  DialogClose,
  InterceptDialog,
  Radio,
  Typography,
} from "@hellocarbo/lasagne";
import { use, useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";

import { PortalContainerContext, UserContext } from "@/contexts";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";
import { TCampaignDuration } from "@/types";
import {
  calculateNextCampaignStartDate,
  formatEndDate,
} from "@/utils/formatDate";

type DateAndDurationProps = {
  prevCampaignEndDate: Date;
  duration: TCampaignDuration;
  onDateChange: (value: Date) => void;
  onDurationChange: (value: TCampaignDuration) => void;
  onButtonClick: () => void;
};

function DateAndDuration({
  prevCampaignEndDate,
  duration,
  onDateChange,
  onDurationChange,
  onButtonClick,
}: DateAndDurationProps) {
  // Load the "options" namespace before redirecting to the subscription page (for breadcrumb translation)
  const { t, i18n } = useTranslation(["common", "trackingCampaign", "options"]);
  const { container } = useContext(PortalContainerContext);
  const { user } = use(UserContext);
  const { sendSegmentEvent } = useSegmentEvent();
  const outboundQueryParams = useOutboundQueryParams("upgrade");

  const advicedStartDate = calculateNextCampaignStartDate(prevCampaignEndDate);

  const canCreateTrackingCampaign = user?.policies?.score.queries["create"];
  const isPaused = user?.company.subscriptionType === "pause";

  return (
    <>
      <Typography className="mb-lg">
        <Trans
          t={t}
          i18nKey="trackingCampaign:new.description"
          values={{
            prevEndDate: t("formatter:date", {
              value: formatEndDate(prevCampaignEndDate),
            }),
            newStartDate: t("formatter:date", {
              value: advicedStartDate,
            }),
          }}
          components={{
            1: <span className="text-primary font-bold" />,
          }}
        />
      </Typography>
      <div className="gap-y-lg flex flex-col">
        <DateField
          name="startDate"
          language={i18n.language as "fr" | "en"}
          defaultValue={advicedStartDate}
          label={t("trackingCampaign:footprintStartDate")}
          popoverProps={{ portalContainer: container as Element }}
          onChange={onDateChange}
        />
        <Radio
          type="button"
          name="duration"
          defaultValue={duration}
          label={t("trackingCampaign:footprintDuration")}
          items={Object.values(TCampaignDuration).map((duration) => ({
            label: t(`trackingCampaign:durations.${duration}`),
            value: duration,
          }))}
          onValueChange={(newDuration) =>
            onDurationChange(newDuration as TCampaignDuration)
          }
        />
        <div className="text-center">
          <InterceptDialog
            show={!canCreateTrackingCampaign}
            title={t("trackingCampaign:upsellModal.title")}
            description={t(
              isPaused
                ? "trackingCampaign:upsellModal.pauseDescription"
                : "trackingCampaign:upsellModal.description"
            )}
            warningMessage={t(
              isPaused
                ? "trackingCampaign:upsellModal.pauseWarningMessage"
                : "trackingCampaign:upsellModal.warningMessage"
            )}
            footer={
              <DialogClose asChild>
                <Button
                  as={Link}
                  to={
                    isPaused
                      ? `https://try.hellocarbo.com/meetings/emmanuel-watrinet/rdv-abonnement-carbo-entreprise${outboundQueryParams}`
                      : "/options/subscription?option=score"
                  }
                  onClick={() =>
                    sendSegmentEvent(
                      isPaused ? "pause_reactivation" : "option_subscription"
                    )
                  }
                  shouldOpenNewTab={isPaused}
                >
                  {t(
                    isPaused
                      ? "trackingCampaign:upsellModal.pauseCta"
                      : "common:requestSubscription"
                  )}
                </Button>
              </DialogClose>
            }
            portalContainer={container as Element}
          >
            <Button onClick={onButtonClick}>{t("common:next")}</Button>
          </InterceptDialog>
        </div>
      </div>
    </>
  );
}

export default DateAndDuration;
